import { useQuery } from "@apollo/client";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button, Select } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import Breadcrumb from "../../components/Breadcrumb";
import { EmptyState, ErrorState } from "../../components/states";
import { useToast } from "../../components/Toast/toastHook";
import BuyerNeedCard from "../../components/upcomingListings/BuyerNeedCard";
import BuyerNeedsSkeleton from "../../components/upcomingListings/BuyerNeedsSkeleton";
import MeetingMode from "../../components/upcomingListings/MeetingMode";
import OpenHousesTab from "../../components/openHouses/OpenHousesTab";
import UpcomingListingCard from "../../components/upcomingListings/UpcomingListingCard";
import UpcomingListingsSkeleton from "../../components/upcomingListings/UpcomingListingsSkeleton";
import UpcomingTabs from "../../components/upcomingListings/UpcomingTabs";
import { formatAgentName } from "../../components/upcomingListings/listingDisplay";
import usePagedRows from "../../hooks/usePagedRows";
import { GET_COMPANY_USERS } from "../../graphql/companyQueries";
import { GET_COMPANY_BUYER_NEEDS } from "../../graphql/buyerNeedsQueries";
import {
  GET_OPEN_HOUSE_HOST_COUNTS,
  GET_UPCOMING_LISTINGS,
} from "../../graphql/listingQueries";
import {
  ALL_AGENTS,
  DEFAULT_NEEDS_SORT,
  DEFAULT_SORT,
  LISTINGS_PAGE_SIZE,
  NEEDS_PAGE_SIZE,
  NEEDS_SORT_OPTIONS,
  TABS,
  UPCOMING_SORT_OPTIONS,
  UPCOMING_WINDOW_DAYS,
} from "../../constants/upcomingListingsConstants";

/**
 * Upcoming Listings — the office's not-yet-live listings and what its buyers are
 * looking for, side by side, for a broker to walk through in a sales meeting.
 *
 * Tab, agent and sort all live in the query string so a broker can bookmark
 * "my agent's listings" and land straight on it when the meeting starts. One
 * agent filter drives both tabs: picking an agent on Listings and switching to
 * Buyer Needs keeps you on the same person.
 *
 * Both tabs load on arrival rather than lazily, because the tab badges have to
 * show real counts before either tab is opened.
 */
const UpcomingListings = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const toast = useToast(4000);
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedTab = searchParams.get("tab");
  const tab = Object.values(TABS).includes(requestedTab)
    ? requestedTab
    : TABS.LISTINGS;
  const agentId = searchParams.get("agent") || ALL_AGENTS;
  const sortBy = searchParams.get("sort") || DEFAULT_SORT;
  const needsSortBy = searchParams.get("needsSort") || DEFAULT_NEEDS_SORT;

  const setParam = (key, value, fallback) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === fallback) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const agentFilter = agentId === ALL_AGENTS ? undefined : agentId;

  const listings = usePagedRows({
    query: GET_UPCOMING_LISTINGS,
    field: "getCompanyListings",
    rowsKey: "listing",
    pageSize: LISTINGS_PAGE_SIZE,
    resetKey: `${agentId}|${sortBy}`,
    variables: {
      companyId,
      listingInput: {
        upcomingOnly: true,
        upcomingWithinDays: UPCOMING_WINDOW_DAYS,
        ...(agentFilter && { agentId: agentFilter }),
      },
      sortBy,
    },
  });

  const needs = usePagedRows({
    query: GET_COMPANY_BUYER_NEEDS,
    field: "getCompanyBuyerNeeds",
    rowsKey: "criteria",
    pageSize: NEEDS_PAGE_SIZE,
    resetKey: `${agentId}|${needsSortBy}`,
    variables: {
      companyId,
      sortBy: needsSortBy,
      ...(agentFilter && { agentId: agentFilter }),
    },
  });

  // Counts only: the badge has to be right before the tab is ever opened, and
  // the tab itself re-queries when it mounts.
  const { data: openHouseCounts } = useQuery(GET_OPEN_HOUSE_HOST_COUNTS, {
    variables: { companyId, withinDays: UPCOMING_WINDOW_DAYS },
    fetchPolicy: "cache-and-network",
  });

  const { data: usersData } = useQuery(GET_COMPANY_USERS, {
    variables: { companyId, sorted: true },
    fetchPolicy: "cache-first",
  });

  const agentOptions = useMemo(() => {
    const users = (usersData?.getCompanyUsers ?? [])
      .filter((u) => !u.deletedAt && u.isActive)
      .map((u) => ({ value: u.id, label: formatAgentName(u) || u.email }))
      .filter((o) => o.label);

    return [{ value: ALL_AGENTS, label: "All Agents" }, ...users];
  }, [usersData]);

  const selectedAgentLabel =
    agentId === ALL_AGENTS
      ? null
      : agentOptions.find((o) => o.value === agentId)?.label;

  const presenting = searchParams.get("present") === "1";
  const startId = searchParams.get("start") || null;

  const exitPresenting = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("present");
    next.delete("start");
    setSearchParams(next, { replace: true });
  };

  const startPresenting = () => {
    const next = new URLSearchParams(searchParams);
    next.set("present", "1");
    setSearchParams(next);
  };

  const onListings = tab === TABS.LISTINGS;
  const onOpenHouses = tab === TABS.OPEN_HOUSES;
  // Open Houses owns its own query, so it is not one of the paged lists here.
  const active = onListings ? listings : needs;

  useEffect(() => {
    if (!onOpenHouses && active.error) toast("error", active.error.message);
  }, [onOpenHouses, active.error, toast]);

  const clearAgent = () => setParam("agent", ALL_AGENTS, ALL_AGENTS);

  // The filters travel with the click, so the detail view's Prev / Next walks
  // the same set the broker was just looking at.
  const openListing = (listing) => {
    const query = searchParams.toString();
    navigate(
      `/company/${companyId}/upcoming-listings/${listing._id}${query ? `?${query}` : ""}`
    );
  };

  const loadMore = (
    <div className="mt-6 flex justify-center">
      <Button
        loading={active.loading}
        onClick={active.loadMore}
        style={{ borderColor: "#b4c540", color: "#96aa10" }}
      >
        Load more ({active.remaining})
      </Button>
    </div>
  );

  const renderListings = () => {
    if (listings.firstLoad) {
      return <UpcomingListingsSkeleton cards={LISTINGS_PAGE_SIZE} />;
    }

    if (listings.error && !listings.rows.length) {
      return <ErrorState onRetry={() => listings.refetch()} />;
    }

    if (!listings.rows.length) {
      return selectedAgentLabel ? (
        <EmptyState
          title="No upcoming listings for this agent"
          description={`${selectedAgentLabel} has nothing going live in the next ${UPCOMING_WINDOW_DAYS} days.`}
          actionLabel="Show all agents"
          onAction={clearAgent}
        />
      ) : (
        <EmptyState
          title="No upcoming listings"
          description={`Nothing in this office goes live on MLS in the next ${UPCOMING_WINDOW_DAYS} days.`}
        />
      );
    }

    return (
      <>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {listings.rows.map((listing) => (
            <UpcomingListingCard
              key={listing._id}
              listing={listing}
              onSelect={openListing}
            />
          ))}
        </div>
        {listings.hasMore && loadMore}
      </>
    );
  };

  const renderNeeds = () => {
    if (needs.firstLoad) return <BuyerNeedsSkeleton rows={8} />;

    if (needs.error && !needs.rows.length) {
      return <ErrorState onRetry={() => needs.refetch()} />;
    }

    if (!needs.rows.length) {
      return selectedAgentLabel ? (
        <EmptyState
          title="No buyer needs for this agent"
          description={`${selectedAgentLabel} has no buyers with wants & needs on file.`}
          actionLabel="Show all agents"
          onAction={clearAgent}
        />
      ) : (
        <EmptyState
          title="No buyer needs"
          description="Wants & needs added by this office's agents will appear here."
        />
      );
    }

    return (
      <>
        <p className="-mt-2 mb-3 text-xs text-gray-400">
          {selectedAgentLabel
            ? `What ${selectedAgentLabel}'s buyers are looking for`
            : "What this office's buyers are looking for"}
        </p>
        <div className="overflow-hidden rounded-lg border border-light_gray bg-white shadow-card">
          {needs.rows.map((criteria) => (
            <BuyerNeedCard key={criteria._id} criteria={criteria} />
          ))}
        </div>
        {needs.hasMore && loadMore}
      </>
    );
  };

  return (
    <div id="upcoming-listings" className="pb-10">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <Breadcrumb pageName="Upcoming Listings" />
        {/* Meeting Mode walks the Listings tab. Offering it from Open Houses
            would present a different set than the one on screen. */}
        {!onOpenHouses && (
          <Button
            type="primary"
            disabled={listings.totalCount === 0}
            onClick={startPresenting}
            style={{
              backgroundColor: listings.totalCount ? "#96aa10" : undefined,
              borderColor: listings.totalCount ? "#96aa10" : undefined,
            }}
          >
            {/* Naming the agent makes it obvious what the meeting is about to
                show — the button follows whatever filter is applied. */}
            ▶ Present
            {selectedAgentLabel ? ` ${selectedAgentLabel.split(" ")[0]}'s` : ""}
          </Button>
        )}
      </div>

      <p className="mb-5 text-sm text-gray-400 sm:-mt-3">
        {onOpenHouses
          ? `Open houses scheduled in the next ${UPCOMING_WINDOW_DAYS} days.`
          : `Listings going live on MLS in the next ${UPCOMING_WINDOW_DAYS} days.`}
      </p>

      <UpcomingTabs
        tab={tab}
        onChange={(next) => setParam("tab", next, TABS.LISTINGS)}
        tabs={[
          { key: TABS.LISTINGS, label: "Listings", count: listings.totalCount },
          { key: TABS.NEEDS, label: "Buyer Needs", count: needs.totalCount },
          {
            key: TABS.OPEN_HOUSES,
            label: "Open Houses",
            count:
              openHouseCounts?.getCompanyUpcomingOpenHouses?.totalCount ?? 0,
          },
        ]}
      />

      {!onOpenHouses && (
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <Select
          showSearch
          optionFilterProp="label"
          value={agentId}
          onChange={(value) => setParam("agent", value, ALL_AGENTS)}
          options={agentOptions}
          style={{ minWidth: 200 }}
          aria-label="Filter by agent"
        />

        {onOpenHouses ? null : onListings ? (
          <Select
            value={sortBy}
            onChange={(value) => setParam("sort", value, DEFAULT_SORT)}
            options={UPCOMING_SORT_OPTIONS}
            style={{ minWidth: 180 }}
            aria-label="Sort listings"
          />
        ) : (
          <Select
            value={needsSortBy}
            onChange={(value) => setParam("needsSort", value, DEFAULT_NEEDS_SORT)}
            options={NEEDS_SORT_OPTIONS}
            style={{ minWidth: 180 }}
            aria-label="Sort buyer needs"
          />
        )}

        {selectedAgentLabel && (
          <button
            type="button"
            onClick={clearAgent}
            className="inline-flex items-center gap-2 rounded-sm border border-primary bg-[#f4f7e4] px-3 py-1 text-xs font-bold text-primary_dark"
          >
            Agent: {selectedAgentLabel}
            <CloseOutlined style={{ fontSize: 10 }} />
          </button>
        )}

        {!onOpenHouses && (
          <span className="ml-auto rounded-sm border border-light_gray bg-white px-3 py-1 text-xs text-secondary">
            <b className="text-primary_dark">{active.totalCount}</b>{" "}
            {onListings ? "upcoming" : "buyer needs"}
          </span>
        )}
      </div>
      )}

      {onOpenHouses ? (
        <OpenHousesTab
          companyId={companyId}
          agentId={agentId}
          agentOptions={agentOptions}
          onAgentChange={(value) => setParam("agent", value, ALL_AGENTS)}
          selectedAgentLabel={selectedAgentLabel}
          onClearAgent={clearAgent}
        />
      ) : onListings ? (
        renderListings()
      ) : (
        renderNeeds()
      )}

      {presenting && (
        <MeetingMode
          companyId={companyId}
          agentId={agentId}
          agentLabel={selectedAgentLabel}
          sortBy={sortBy}
          startId={startId}
          onExit={exitPresenting}
        />
      )}
    </div>
  );
};

export default UpcomingListings;
